/**
 * card-stack.js — 3D perspective card deck for !CON
 *
 * Usage (HTML):
 *
 *   <div class="card-stack-scene" data-card-stack data-category="Ready to Wear">
 *     <div class="card-stack">
 *       <a class="card-stack__card" data-card href="/products/piece-01">
 *         <img src="..." alt="Piece 01" loading="lazy">
 *         <span class="card-stack__label">Piece 01</span>
 *       </a>
 *       <!-- more cards … -->
 *     </div>
 *     <span class="card-stack__counter" data-card-counter></span>
 *     <span class="card-stack__category" data-card-category></span>
 *   </div>
 *
 * The container can be the scene itself or any ancestor; the class searches
 * downward for `.card-stack` (the deck wrapper) and `[data-card]` items.
 */

(function () {
  'use strict';

  // ------------------------------------------------------------------
  // Constants — match CSS custom-property defaults as JS fallbacks
  // ------------------------------------------------------------------
  const FAN_X = 72;    // px per card step (rightward — deeper cards fan right)
  const FAN_Y = 68;    // px per card step (upward — deeper cards rise)
  const FAN_Z = 0;     // no Z recession (flat 2-D fan)
  const FAN_RY = 0;    // no Y-axis rotation (flat fan)

  // Maximum number of cards shown in the fanned stack behind the front card.
  // Cards beyond this are simply hidden until they cycle to the front.
  const MAX_VISIBLE_BEHIND = 5;

  // Minimum milliseconds between wheel-triggered advances (debounce)
  const WHEEL_DEBOUNCE = 450;

  // ------------------------------------------------------------------
  // CardStack class
  // ------------------------------------------------------------------
  class CardStack {
    /**
     * @param {HTMLElement} container — element with [data-card-stack]
     */
    constructor(container) {
      this.container = container;

      // Find the deck wrapper (may be the container itself or a child)
      this.deck = container.querySelector('.card-stack') || container;

      // Collect all card elements in DOM order
      this.cards = Array.from(container.querySelectorAll('[data-card]'));

      if (this.cards.length === 0) {
        console.warn('[CardStack] No [data-card] elements found inside', container);
        return;
      }

      this.total = this.cards.length;
      this.current = 0; // index of the front card
      this._wheelLastAt   = 0;
      this._wheelLocked   = false;
      this._wheelMinDelta = Infinity;
      this._wheelInertia  = null;
      this._animating = false;

      // UI elements
      this.counterEl = container.querySelector('[data-card-counter]');
      this.categoryEl = container.querySelector('[data-card-category]');

      // Optional: category text from data attribute on container
      const categoryText = container.dataset.category || '';
      if (this.categoryEl && categoryText) {
        this.categoryEl.textContent = categoryText;
      }

      this._init();
    }

    // ----------------------------------------------------------------
    // Initialisation
    // ----------------------------------------------------------------
    _init() {
      // On mobile the full 72px fan spread is wider than the viewport, which
      // pushes the front card off-screen when we centre the mass.  Use a
      // tighter spread so all cards stay visible and the mass sits mid-screen.
      const isMobile = window.innerWidth <= 640;
      this.fanX = isMobile ? 40 : FAN_X;
      this.fanY = isMobile ? 0  : FAN_Y; // mobile: horizontal-only fan, no vertical lift

      // Only up to MAX_VISIBLE_BEHIND cards are ever fanned out behind the
      // front card at any one time — every card deeper than that parks in a
      // single fixed spot (see _layoutCard's "hidden" branch below) instead
      // of fanning out further. The centering offset must be based on that
      // capped fan width, NOT the full catalog size (this.total) — otherwise
      // every extra product added to the stack keeps pushing the whole deck
      // further left, eventually running the front card off-screen on
      // mobile, which is exactly what was happening here.
      const visibleDepth = Math.min(this.total - 1, MAX_VISIBLE_BEHIND);

      // Shift the deck so the centre of the visible fanned group is centred
      // in the scene. On desktop add an extra 5vw nudge for the scroll-wheel.
      // On mobile, nudge right instead so the front card sits comfortably
      // clear of the left edge rather than being crowded by the fan tail —
      // then pull back left by 15vw so the card doesn't sit too far right.
      const groupOffset = isMobile
        ? -(visibleDepth * this.fanX) / 2 + 60 - window.innerWidth * 0.15
        : -(visibleDepth * FAN_X)     / 2 - window.innerWidth * 0.05;
      this.deck.style.marginLeft = groupOffset + 'px';

      // Lay out cards in their initial fanned positions
      this._layoutAll();

      // Update counter display
      this._updateCounter();

      // Bind event listeners
      this._bindEvents();
    }

    // ----------------------------------------------------------------
    // Layout — compute and apply CSS transforms to every card
    // ----------------------------------------------------------------
    _layoutAll() {
      this.cards.forEach((card, i) => {
        this._layoutCard(card, i);
      });
    }

    /**
     * Position a single card based on its position relative to `this.current`.
     * @param {HTMLElement} card
     * @param {number} i  — absolute index in this.cards
     */
    _layoutCard(card, i) {
      const total = this.total;
      const current = this.current;

      // Distance behind the front card, wrapping around the deck
      // 0 = active/front, 1 = directly behind, 2 = further back, etc.
      // Negative would be "ahead" — we keep those hidden.
      const depth = (i - current + total) % total;

      // Remove all state classes first
      card.classList.remove('is-active', 'is-out', 'is-visible');
      card.style.removeProperty('--card-z');

      if (depth === 0) {
        // Front card
        card.classList.add('is-active', 'is-visible');
        card.style.setProperty('--card-z', '100');
        card.style.transform = '';
      } else if (depth <= MAX_VISIBLE_BEHIND) {
        // Fanned cards behind front — shift RIGHT and UP
        card.classList.add('is-visible');

        const fanX = this.fanX || FAN_X;
        const fanY = this.fanY || FAN_Y;
        const scale = Math.max(0.85, 1 - depth * 0.05);
        const tx =  fanX * depth;  // POSITIVE = fan RIGHT as depth increases
        const ty = -fanY * depth;  // NEGATIVE = rise upward as depth increases
        const tz = -FAN_Z * depth;
        const ry =  FAN_RY * depth;
        const zIndex = 100 - depth;

        card.style.setProperty('--card-z', String(zIndex));
        card.style.transform =
          `translateX(${tx}px) translateY(${ty}px) translateZ(${tz}px) rotateY(${ry}deg) scale(${scale})`;
      } else {
        // Hidden — parked beyond the visible fan (far right, far up)
        const fanX = this.fanX || FAN_X;
        const fanY = this.fanY || FAN_Y;
        const zIndex = Math.max(0, 100 - depth);
        card.style.setProperty('--card-z', String(zIndex));
        const tx =  fanX * (MAX_VISIBLE_BEHIND + 1);
        const ty = -fanY * (MAX_VISIBLE_BEHIND + 1);
        const tz = -FAN_Z * (MAX_VISIBLE_BEHIND + 1);
        const ry =  FAN_RY * (MAX_VISIBLE_BEHIND + 1);
        card.style.transform =
          `translateX(${tx}px) translateY(${ty}px) translateZ(${tz}px) rotateY(${ry}deg)`;
      }
    }

    // ----------------------------------------------------------------
    // Advance / retreat
    // ----------------------------------------------------------------

    /**
     * Advance to the next card (current flies out, next becomes front).
     */
    next() {
      if (this._animating) return;
      this._animating = true;

      const outCard = this.cards[this.current];

      // Trigger exit animation
      outCard.classList.remove('is-active');
      outCard.classList.add('is-out');

      // Advance index
      this.current = (this.current + 1) % this.total;

      // Re-layout all cards for new positions (transitions handle animation)
      this._layoutAll();

      // Force the outgoing card to keep is-out (layoutAll would have removed it)
      outCard.classList.remove('is-active', 'is-visible');
      outCard.classList.add('is-out');
      outCard.style.setProperty('--card-z', '200');

      this._updateCounter();

      // After transition ends, clean up the exiting card
      const onEnd = () => {
        outCard.removeEventListener('transitionend', onEnd);
        outCard.classList.remove('is-out');
        // Re-apply its correct hidden position
        const i = this.cards.indexOf(outCard);
        this._layoutCard(outCard, i);
        this._animating = false;
      };
      outCard.addEventListener('transitionend', onEnd);

      // Safety: if transitionend doesn't fire (e.g. display:none), unlock anyway
      setTimeout(() => {
        if (this._animating) {
          outCard.removeEventListener('transitionend', onEnd);
          outCard.classList.remove('is-out');
          const i = this.cards.indexOf(outCard);
          this._layoutCard(outCard, i);
          this._animating = false;
        }
      }, 600);
    }

    /**
     * Go back to the previous card (reverse cycle).
     */
    prev() {
      if (this._animating) return;
      this._animating = true;

      // Move index back
      const prevIndex = (this.current - 1 + this.total) % this.total;
      const inCard = this.cards[prevIndex];

      // Place the incoming card in the "out" position before making it visible
      inCard.classList.remove('is-active', 'is-visible');
      inCard.classList.add('is-out');
      inCard.style.setProperty('--card-z', '200');

      // Force a reflow so the browser registers the is-out position before transitioning
      void inCard.offsetWidth;

      this.current = prevIndex;
      this._layoutAll();

      // The new front card should animate in from is-out — remove is-out after layout
      inCard.classList.remove('is-out');
      inCard.classList.add('is-active', 'is-visible');
      inCard.style.setProperty('--card-z', '100');
      inCard.style.transform = '';

      this._updateCounter();

      // Unlock after transition
      setTimeout(() => {
        this._animating = false;
      }, 450);
    }

    // ----------------------------------------------------------------
    // Counter
    // ----------------------------------------------------------------
    _updateCounter() {
      if (this.counterEl) {
        this.counterEl.textContent = `${this.current + 1} / ${this.total}`;
      }
      // Broadcast card change so other UI elements (text panel, etc.) can react
      this.container.dispatchEvent(new CustomEvent('cardstack:change', {
        bubbles: true,
        detail: { index: this.current, total: this.total }
      }));
    }

    // ----------------------------------------------------------------
    // Event binding
    // ----------------------------------------------------------------
    _bindEvents() {
      // Wheel — valley-rise detection (mirrors scroll-wheel-b.liquid)
      // Lock on first event; track lowest delta seen. When delta has dipped
      // ≤8 then rises ≥14, that's a new intentional gesture → advance again.
      // Fallback: 80 ms of silence unlocks (natural momentum end, no re-swipe).
      this.container.addEventListener('wheel', (e) => {
        e.preventDefault();
        e.stopPropagation(); // prevent scroll-wheel-b's document listener from also firing
        const d = Math.abs(e.deltaY !== 0 ? e.deltaY : e.deltaX);

        if (this._wheelLocked) {
          if (d < this._wheelMinDelta) this._wheelMinDelta = d;

          if (this._wheelMinDelta <= 8 && d >= 14) {
            // Valley-rise: new gesture detected — unlock and fall through
            this._wheelLocked   = false;
            this._wheelMinDelta = Infinity;
            clearTimeout(this._wheelInertia);
          } else {
            // Still momentum — swallow, reset silence timer
            clearTimeout(this._wheelInertia);
            this._wheelInertia = setTimeout(() => {
              this._wheelLocked   = false;
              this._wheelMinDelta = Infinity;
            }, 80);
            return;
          }
        }

        if (d < 2) return;

        this._wheelLocked   = true;
        this._wheelMinDelta = d;
        clearTimeout(this._wheelInertia);
        this._wheelInertia = setTimeout(() => {
          this._wheelLocked   = false;
          this._wheelMinDelta = Infinity;
        }, 80);

        if ((e.deltaY !== 0 ? e.deltaY : e.deltaX) > 0) {
          this.next();
        } else {
          this.prev();
        }
      }, { passive: false });

      // Touch swipe — finger drag on touch screens advances/retreats the deck.
      // Dominant axis wins (left/up = next, right/down = prev).
      // Mouse pointers are ignored here; wheel handles those.
      {
        let swipeX = 0, swipeY = 0, didSwipe = false;

        this.container.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'mouse') return;
          swipeX   = e.clientX;
          swipeY   = e.clientY;
          didSwipe = false;
        });

        this.container.addEventListener('pointerup', (e) => {
          if (e.pointerType === 'mouse') return;
          const dx    = e.clientX - swipeX;
          const dy    = e.clientY - swipeY;
          const absDx = Math.abs(dx);
          const absDy = Math.abs(dy);
          if (absDx < 40 && absDy < 40) return; // tap, not swipe
          didSwipe = true;
          if (absDx >= absDy) {
            dx < 0 ? this.next() : this.prev(); // swipe left=next, right=prev
          } else {
            dy < 0 ? this.next() : this.prev(); // swipe up=next, down=prev
          }
        });

        this.container.addEventListener('pointercancel', () => { didSwipe = false; });

        // Suppress link navigation when the gesture was a swipe, not a tap
        this.container.addEventListener('click', (e) => {
          if (didSwipe) { e.preventDefault(); e.stopPropagation(); didSwipe = false; }
        }, true);
      }

      // Click on card — navigate to card href (if the element is an <a> the
      // browser handles it, but we also support non-anchor cards with data-href)
      this.cards.forEach((card) => {
        card.addEventListener('click', (e) => {
          // Only navigate if this is the active/front card
          if (!card.classList.contains('is-active')) {
            e.preventDefault();
            return;
          }
          const href = card.href || card.dataset.href;
          if (href) {
            window.location.href = href;
          }
        });
      });

      // Keyboard — left / right arrows
      document.addEventListener('keydown', (e) => {
        // Only respond if the card stack is (partially) visible in the viewport
        if (!this._isInViewport()) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          this.next();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.prev();
        }
      });

      // External index change — e.g. from scroll-wheel UI
      // Own dispatches include `total`; external ones don't — use that to avoid loops.
      this.container.addEventListener('cardstack:change', (e) => {
        if (!e.detail || e.detail.total != null) return; // ignore own events
        const idx = e.detail.index;
        if (idx == null || idx === this.current) return;
        // Pick the shorter direction (handles wrap-around correctly)
        const fwd = (idx - this.current + this.total) % this.total;
        const bwd = (this.current - idx + this.total) % this.total;
        if (fwd <= bwd) {
          this.next();
        } else {
          this.prev();
        }
      });
    }

    // ----------------------------------------------------------------
    // Helpers
    // ----------------------------------------------------------------
    _isInViewport() {
      const rect = this.container.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }
  }

  // ------------------------------------------------------------------
  // Auto-initialise all [data-card-stack] elements on the page
  // ------------------------------------------------------------------
  function initAll() {
    document.querySelectorAll('[data-card-stack]').forEach((el) => {
      new CardStack(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Expose to global scope for manual use / Shopify section JS hooks
  window.CardStack = CardStack;
})();
